import { useEffect, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { motion } from 'framer-motion';
import { Menu, X } from 'lucide-react';
import { cn } from '@/lib/cn';
import { LanguageSwitcher } from './LanguageSwitcher';

export function SiteHeader() {
  const { t } = useTranslation();
  const loc = useLocation();
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);

  // Header densifies once the user has scrolled — the blob-y hero
  // doesn't need a hard top border, but the rest of the page does.
  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 8);
    onScroll();
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  const navItem = (to: string, label: string) => (
    <Link
      to={to}
      className={cn(
        'relative text-sm font-medium transition-colors',
        loc.pathname === to ? 'text-foreground' : 'text-foreground/65 hover:text-foreground',
      )}
    >
      {label}
      {loc.pathname === to && (
        <motion.span
          layoutId="nav-active"
          className="absolute -bottom-1.5 start-0 end-0 h-0.5 rounded-full bg-gradient-to-r from-indigo-500 to-fuchsia-500"
        />
      )}
    </Link>
  );

  return (
    <header
      className={cn(
        'sticky top-0 z-30 transition-all duration-300',
        scrolled ? 'glass-strong shadow-soft border-b border-border' : 'border-b border-transparent bg-background/40 backdrop-blur',
      )}
    >
      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 h-16 flex items-center justify-between">
        <Link to="/" className="flex items-center gap-2.5">
          <Logo />
          <span className="font-display text-lg font-semibold tracking-tight">{t('brand.name')}</span>
        </Link>

        <nav className="hidden md:flex items-center gap-8">
          {navItem('/pricing', t('nav.pricing'))}
          {navItem('/compare', t('nav.compare'))}
          {navItem('/demo', t('nav.demo'))}
        </nav>

        <div className="flex items-center gap-2">
          <LanguageSwitcher />
          <Link
            to="/login"
            className="hidden sm:inline-flex items-center px-3 py-2 text-sm font-semibold text-foreground/70 hover:text-foreground transition"
          >
            {t('nav.login')}
          </Link>
          <Link
            to="/signup"
            className="hidden sm:inline-flex gradient-primary items-center px-4 py-2 rounded-xl text-white text-sm font-semibold shadow-glow hover:shadow-glow-pink hover:-translate-y-0.5 active:translate-y-0 transition-all duration-200"
          >
            {t('nav.signup')}
          </Link>
          <button
            onClick={() => setOpen((o) => !o)}
            className="md:hidden inline-flex h-9 w-9 items-center justify-center rounded-lg border border-border hover:bg-muted transition"
            aria-label="menu"
          >
            {open ? <X className="h-4 w-4" /> : <Menu className="h-4 w-4" />}
          </button>
        </div>
      </div>

      {open && (
        <motion.div
          initial={{ opacity: 0, y: -8 }}
          animate={{ opacity: 1, y: 0 }}
          className="md:hidden border-t border-border bg-background"
        >
          <div className="mx-auto max-w-7xl px-4 py-4 flex flex-col gap-3">
            <Link to="/pricing" className="text-sm font-medium" onClick={() => setOpen(false)}>{t('nav.pricing')}</Link>
            <Link to="/compare" className="text-sm font-medium" onClick={() => setOpen(false)}>{t('nav.compare')}</Link>
            <Link to="/demo" className="text-sm font-medium" onClick={() => setOpen(false)}>{t('nav.demo')}</Link>
            <Link to="/login" className="text-sm font-medium" onClick={() => setOpen(false)}>{t('nav.login')}</Link>
            <Link
              to="/signup"
              className="gradient-primary mt-2 inline-flex h-10 items-center justify-center rounded-xl px-4 text-sm font-semibold text-white shadow-glow"
              onClick={() => setOpen(false)}
            >
              {t('nav.signup')}
            </Link>
          </div>
        </motion.div>
      )}
    </header>
  );
}

function Logo() {
  return (
    <img
      src="/logo.png"
      alt="KeenEZ"
      className="h-9 w-9 rounded-xl object-contain"
      width={36}
      height={36}
    />
  );
}
